import { useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API_BASE } from '../services/api';
import { withLanguage } from '../i18n/language';
import { applyEffectiveSessionConfig } from './useOrgSessionLimit';

/**
 * Hook para verificar la expiración de la sesión
 * Verifica periódicamente si la sesión sigue siendo válida
 * OPTIMIZADO: 5 min interval, abort controller para evitar requests apiladas
 */
const useSessionExpiration = (onSessionExpired) => {
  const abortControllerRef = useRef(null);
  const isCheckingRef = useRef(false);

  const checkSession = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    
    if (!token || isCheckingRef.current) {
      return;
    }

    // Abort previous check if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    const controller = new AbortController();
    abortControllerRef.current = controller;
    isCheckingRef.current = true;

    try {
      const response = await axios.post(`${API_BASE}/api/auth/verify`, withLanguage({
        token
      }), { 
        signal: controller.signal,
        timeout: 15000 // 15s timeout
      });

      applyEffectiveSessionConfig(response.data.sessionConfig);

      if (!response.data.valid) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('rfidId');
        if (onSessionExpired) {
          onSessionExpired();
        }
      }
    } catch (error) {
      if (error.name === 'AbortError' || error.name === 'CanceledError') {
        return; // Aborted intentionally
      }
      
      // Error al verificar - probablemente sesión expirada
      if (error.response?.status === 401) {
        localStorage.removeItem('authToken');
        localStorage.removeItem('rfidId');
        if (onSessionExpired) {
          onSessionExpired();
        }
      }
      // For network errors (500, timeout), don't logout - just retry next interval
    } finally {
      isCheckingRef.current = false;
    }
  }, [onSessionExpired]);

  useEffect(() => {
    // Verificar después de 5 segundos (no inmediatamente, evitar ráfaga en mount)
    const initialTimer = setTimeout(checkSession, 5000);

    // Verificar cada 5 minutos (was 2 min - still too aggressive)
    const interval = setInterval(checkSession, 300000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [checkSession]);

  return { checkSession };
};

export default useSessionExpiration;
